import Splide, { type Options } from '@splidejs/splide';
import { Build, Component, Element, Prop, Watch, h } from '@stencil/core';
import { renderTools } from '../../helpers/RenderTools';
import { isEnterKey, openUrl } from '../../helpers/helpers';
import { updateNextArrowState } from '../../helpers/splide-helper';
import type {
  CmsCategorySliderConfig,
  CmsCategorySliderItem,
  SplideComponentConfig,
} from '../../helpers/utils/interfaces';

@Component({
  tag: 'cms-category-slider',
  styleUrl: 'cms-category-slider.scss',
  shadow: true,
})
export class CmsCategorySlider {
  /**
   * Set the Slider configs
   */
  @Prop() config: string;

  /**
   * Set the translations for splide
   */
  @Prop() translations: string;
  private splideTranslations: SplideComponentConfig;

  /**
   * Set the tracking namespace (optional)
   */
  @Prop({ mutable: true }) namespace?: string;

  /**
   * Set the separator for the mobile pagination
   */
  @Prop() separator: string;

  @Watch('config')
  private configWatcher(newValue: CmsCategorySliderConfig | string) {
    if (newValue) {
      this.options =
        typeof newValue === 'string'
          ? (JSON.parse(newValue) as CmsCategorySliderConfig)
          : newValue;
    }
  }

  @Watch('translations')
  private translationsWatcher(newValue: SplideComponentConfig | string) {
    if (newValue) {
      this.splideTranslations =
        typeof newValue === 'string'
          ? (JSON.parse(newValue) as SplideComponentConfig)
          : newValue;
    }
  }

  @Element() host: HTMLDivElement;

  private options: CmsCategorySliderConfig;
  private items: CmsCategorySliderItem[];
  private splideNode: HTMLElement;
  private splideInner: HTMLElement;
  private splide: Splide;
  private currentIndex: number;
  private currentIndexNode: HTMLElement;

  private readonly splideConfig = {
    type: 'slide',
    perMove: 1,
    gap: '1rem',
    autoWidth: true,
    slideFocus: false,
    pagination: false,
    trimSpace: true,
    autoHeight: true,
    focusableNodes: 'li',
    role: 'carousel',
    keyboard: true,
  } as Options;

  componentWillLoad() {
    if (this.config === undefined) {
      return;
    }
    this.configWatcher(this.config);

    this.items = this.options.items.filter(
      (item) => item.name && item.name.trim() !== '',
    );

    if (this.translations !== undefined) {
      this.translationsWatcher(this.translations);
    }
  }

  componentDidLoad() {
    if (this.options.minSlides > this.items.length) {
      return;
    }
    this.splideNode = this.host.shadowRoot.querySelector('.splide');
    this.splideInner = this.splideNode.querySelector('.splide-inner');
    this.currentIndexNode =
      this.host.shadowRoot.querySelector('.current-index');
    this.mountSplide();
  }

  private mountSplide() {
    if (!Build.isBrowser || this.options.items.length <= 0) {
      return;
    }
    this.splide = new Splide(this.splideNode, this.splideConfig);
    this.splide.on('ready moved', () => updateNextArrowState(this.splide));
    this.splide.on('resized visible', this.updateSize.bind(this));
    this.splide.on('mounted move', () => {
      this.currentIndex = this.splide.index;
      this.currentIndexNode.innerHTML = String(this.currentIndex + 1);
    });
    this.splide.mount();
  }

  private generateUniqueId() {
    return Math.floor(Math.random() * Date.now());
  }

  private updateSize() {
    updateNextArrowState(this.splide);

    // Set size to prevent flickering
    this.splideInner.style.height = `${
      this.splideNode.querySelector('.slide').getClientRects()[0].height
    }px`;
  }

  render() {
    if (this.options.minSlides > this.items.length) {
      return (
        <section
          class='invalid-configuration'
          innerHTML={this.options.fallback}
        />
      );
    }

    const sectionStyle = {
      backgroundColor: this.options.background
        ? 'var(--cms-semanticColors-container-medium-background)'
        : 'var(--cms- semanticColors-transparency-strong-background)',
    };

    const headlineColor = {};
    if (this.options.background) {
      // biome-ignore lint/complexity/useLiteralKeys: Type 'any' required
      headlineColor['colorToken'] = '--cms-semanticColors-base-strong-text';
    }

    return (
      <section
        class='category-slider-container bg-image-fluid'
        style={sectionStyle}
      >
        {this.options.headline !== undefined ? (
          <cms-headline
            style={{ '--cms-spacing-top': '0' }}
            headlineText={this.options.headline}
            headlineTag='h2'
            headlineClass='headline'
            headlinePosition='center'
            isAdaptive={true}
            {...headlineColor}
          />
        ) : null}
        <div class={'category-slider-inner'}>
          <div class='splide carousel'>
            <div class='splide-inner'>
              <div class='splide__track track'>
                <ul class='splide__list list'>
                  {this.items.map((item) => (
                    <li
                      onClick={() => openUrl(item.url)}
                      onKeyDown={(event) => {
                        if (isEnterKey(event)) {
                          openUrl(item.url);
                        }
                      }}
                      key={this.generateUniqueId()}
                      class='splide__slide slide'
                      {...{
                        ariaHidden: false,
                        tabIndex: 0,
                      }}
                    >
                      <div class='splide__slide__container slide-container'>
                        <img
                          loading='lazy'
                          width={item.image.width}
                          height={item.image.height}
                          src={item.image.url}
                          alt={item.image.alt}
                          title={item.titleText}
                          class='lazyload category-image'
                        />
                      </div>
                      <div class='button-wrapper'>
                        <cms-link
                          focusable={false}
                          class='detail-link'
                          href=''
                          type='standalone-inverted'
                          linkTitle={item.name}
                          content={item.name}
                          size='sm'
                          spacing='none'
                          tag='span'
                          style={{
                            '--cms-text-color':
                              'var(--cms-semanticColors-base-medium-iconOnBackground)',
                          }}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              <div class='splide__arrows arrows-wrapper'>
                <cms-button
                  namespace={`${this.namespace}-arrow-left`}
                  style={
                    this.options.background
                      ? {
                          '--cms-theme-border-color':
                            'var(--cms-semanticColors-base-strong-border',
                        }
                      : null
                  }
                  class={'splide-arrow splide__arrow--prev'}
                  theme='secondary'
                  size='button-sm'
                  type='button'
                  icon='arrowLeft'
                  icon-size='sm'
                  icon-color={
                    this.options.background
                      ? '--cms-semanticColors-base-strong-icon'
                      : null
                  }
                  focusable={false}
                  tag-title={this.splideTranslations?.arrowLeftLabel}
                />

                <cms-button
                  namespace={`${this.namespace}-arrow-right`}
                  style={
                    this.options.background
                      ? {
                          '--cms-theme-border-color':
                            'var(--cms-semanticColors-base-strong-border',
                        }
                      : null
                  }
                  class={'splide-arrow splide__arrow--next'}
                  theme='secondary'
                  size='button-sm'
                  type='button'
                  icon='arrowRight'
                  icon-size='sm'
                  icon-color={
                    this.options.background
                      ? '--cms-semanticColors-base-strong-icon'
                      : null
                  }
                  focusable={false}
                  tag-title={this.splideTranslations?.arrowRightLabel}
                />
              </div>
              {renderTools.renderMobilePagination(
                this.separator,
                this.items.length,
                this.currentIndex,
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }
}
